import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Heart, Users, Star, Wind, Compass, TrendingUp, Baby, Lock, ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ICONS = { Heart, Users, Star, Wind, Compass, TrendingUp, Baby };

export default function ModulesPage() {
  const navigate = useNavigate();
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadModules(); }, []);

  const loadModules = async () => {
    try {
      const res = await axios.get(`${API}/modules`);
      setModules(res.data.modules);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleClick = (mod) => {
    if (mod.status === "active") navigate(`/chat/${mod.module_id}`);
    else if (localStorage.getItem("lumis_user_token")) navigate(`/chat/${mod.module_id}`);
    else navigate("/register");
  };

  const Icon = ({ name }) => { const C = ICONS[name] || Star; return <C size={22} />; };

  if (loading) return (
    <div className="min-h-screen bg-[#EEF2FF] flex items-center justify-center">
      <div className="text-[#1E3A8A] text-3xl animate-pulse font-outfit">✦</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#EEF2FF] flex flex-col max-w-md mx-auto" data-testid="modules-page">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-[#EEF2FF]/90 backdrop-blur-xl border-b border-gray-200 px-6 py-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-outfit font-light text-gray-900">✦ Lumis</h1>
            <p className="text-gray-500 text-xs font-manrope mt-0.5">Escolha um módulo para começar</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="modules-agenda-btn"
              onClick={() => navigate(localStorage.getItem("lumis_user_token") ? "/agenda" : "/register")}
              className="text-[#1E3A8A] text-xs font-manrope border border-[#1E3A8A]/20 rounded-full px-3 py-1.5 hover:bg-[#DBEAFE] transition-all"
            >
              Agenda
            </button>
            <button
              data-testid="modules-voting-btn"
              onClick={() => navigate("/voting")}
              className="text-[#1E3A8A] text-xs font-manrope border border-[#1E3A8A]/20 rounded-full px-3 py-1.5 hover:bg-[#DBEAFE] transition-all"
            >
              Votar nos próximos
            </button>
          </div>
        </div>
      </div>

      {/* Modules */}
      <div className="flex-1 p-5 space-y-3 pb-10">
        {modules.map((mod, i) => (
          <button
            key={mod.module_id}
            data-testid={`module-card-${mod.module_id}`}
            onClick={() => handleClick(mod)}
            className={`w-full text-left lumis-fade transition-all duration-200 ${
              mod.status === "active"
                ? "bg-white border border-[#1E3A8A]/25 rounded-3xl p-5 shadow-[0_4px_24px_rgba(30,58,138,0.08)] hover:border-[#1E3A8A]/50 hover:shadow-[0_4px_24px_rgba(30,58,138,0.16)] active:scale-[0.99]"
                : "bg-white/60 border border-gray-200 rounded-3xl p-5 hover:bg-white/80 active:scale-[0.99]"
            }`}
            style={{ animationDelay: `${i * 0.05}s` }}
          >
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-2xl shrink-0 ${
                mod.status === "active" ? "bg-[#DBEAFE] text-[#1E3A8A]" : "bg-gray-100 text-gray-400"
              }`}>
                {mod.status === "active" ? <Icon name={mod.icon} /> : <Lock size={20} />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`font-outfit text-sm font-medium ${mod.status === "active" ? "text-gray-900" : "text-gray-400"}`}>
                    {mod.name}
                  </span>
                  {mod.status === "active" && (
                    <span className="text-[10px] bg-[#DBEAFE] text-[#1E3A8A] rounded-full px-2 py-0.5 font-manrope shrink-0">ATIVO</span>
                  )}
                  {mod.status !== "active" && mod.vote_count > 0 && (
                    <span className="text-[10px] bg-gray-100 text-gray-400 rounded-full px-2 py-0.5 font-manrope shrink-0">{mod.vote_count} votos</span>
                  )}
                </div>
                <p className={`text-xs font-manrope mt-0.5 leading-relaxed line-clamp-2 ${
                  mod.status === "active" ? "text-gray-500" : "text-gray-400"
                }`}>
                  {mod.description}
                </p>
              </div>
              <ChevronRight size={15} className={mod.status === "active" ? "text-[#1E3A8A]/70 shrink-0" : "text-gray-300 shrink-0"} />
            </div>
          </button>
        ))}
      </div>

      <div className="px-6 pb-8 text-center">
        <p className="text-gray-500/40 text-xs font-manrope">
          Módulos bloqueados serão abertos conforme a votação.
        </p>
      </div>
    </div>
  );
}
